import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import IsilonDocAnalyzer from './isilon-doc';

declare const Bun: {
  file(path: string): {
    exists(): Promise<boolean>;
    text(): Promise<string>;
  };
  write(path: string, content: string): Promise<void>;
};

interface Reference {
  text: string;
  isUrl: boolean;
}

interface Message {
  type: string;
  message: string;
  rawLine: string;
}

interface Check {
  name: string;
  status: string;
  messages: Message[];
  rawDetails: string;
  additionalInfo?: string;
}

interface Categories {
  FAIL: Check[];
  WARN: Check[];
  INFO: Check[];
  PASS: Check[];
}

interface AnalysisResults {
  clusterInfo: {
    name: string;
    clusterGuid: string;
    nodeCount: string;
    currentVersion: string;
    targetVersion: string;
    iocaVersion: string;
    upgradePlan: string;
    estimatedDuration: string;
    nodeTypes: string[];
  };
  categories: {
    PASS: Check[];
    WARN: Check[];
    FAIL: Check[];
    INFO: Check[];
  };
  displayName?: string;
}

interface StatsData {
  clusterCount: number;
  totalChecks: number;
  totalPassChecks: number;
  totalWarnChecks: number;
  totalFailChecks: number;
  totalInfoChecks: number;
  healthDistribution: number[];
  onefsVersions: Record<string, number>;
  iocaVersions: Record<string, number>;
  commonWarnings: Record<string, number>;
  commonFailures: Record<string, number>;
  commonInfos: Record<string, number>;
  nodeTypes: Record<string, number>;
  srsEnabled: number;
  srsDisabled: number;
  versionsData?: Array<{name: string; value: number}>;
  iocaVersionsData?: Array<{name: string; value: number}>;
  nodeTypesData?: Array<{name: string; value: number}>;
  warningsData?: Array<{name: string; count: number}>;
  failuresData?: Array<{name: string; count: number}>;
}

interface NodeTypes {
  [key: string]: string[];
}

interface NodeTypeData {
  name: string;
  value: number;
}

const IsilonLogAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [logData, setLogData] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [analysisName, setAnalysisName] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [rawDataModalOpen, setRawDataModalOpen] = useState(false);
  const [currentRawData, setCurrentRawData] = useState('');
  const [statsData, setStatsData] = useState(null);
  const [activeModule, setActiveModule] = useState('log-analyzer');
  const [globalTab, setGlobalTab] = useState<string | null>(null);

  // Documentation Analyzer states
  const [docData, setDocData] = useState<any | null>(null);
  const [docActiveTab, setDocActiveTab] = useState('upload');
  const [docError, setDocError] = useState<string | null>(null);
  const [savedDocs, setSavedDocs] = useState<any[]>([]);
  const [docName, setDocName] = useState('');
  const [showRawData, setShowRawData] = useState(false);

  const [customNodeTypes, setCustomNodeTypes] = useState<NodeTypes>(() => {
    const savedTypes = localStorage.getItem('customNodeTypes');
    return savedTypes ? JSON.parse(savedTypes) : {
      'X-Series': ['X210', 'X400'],
      'H-Series': ['H500', 'H600', 'H700'],
      'A-Series': ['A200', 'A300', 'A3000'],
      'F-Series': ['F210', 'F710', 'F800', 'F900', 'F910'],
      'NL-Series': ['NL400', 'NL410'],
      'P-Series': ['P100'],
      'B-Series': ['B100']
    };
  });

  // Aktualisiere die updateNodeTypes Funktion
  const updateNodeTypes = (series: string, types: string[]) => {
    const updatedTypes = {
      ...customNodeTypes,
      [series]: types
    };
    setCustomNodeTypes(updatedTypes);
    localStorage.setItem('customNodeTypes', JSON.stringify(updatedTypes));
  };

  // Aktualisiere die isValidNodeType Funktion
  const isValidNodeType = (type: string): boolean => {
    // Basis Node Type Pattern - erweitert um NL, P und B
    const nodeTypePattern = /^([XHAF]|NL|[PB])\d{3,4}$/;
    
    // Wenn es dem Basis-Pattern entspricht, prüfe gegen benutzerdefinierte Typen
    if (nodeTypePattern.test(type)) {
      return Object.values(customNodeTypes).flat().includes(type);
    }
    
    return false;
  };

  // Funktion zum Zählen von Einträgen in den Log-Ergebnissen
  const countResults = (results) => {
    if (!results || !results.categories) return { PASS: 0, WARN: 0, FAIL: 0, INFO: 0, total: 0 };
    
    const counts = {
      PASS: results.categories.PASS.length,
      WARN: results.categories.WARN.length,
      FAIL: results.categories.FAIL.length,
      INFO: results.categories.INFO.length,
      total: 0
    };
    
    counts.total = counts.PASS + counts.WARN + counts.FAIL + counts.INFO;
    return counts;
  };

  // Öffne die Detailansicht für einen bestimmten Eintrag
  const openDetails = (item) => {
    setSelectedItem(item);
    setShowDetails(true);
  };
  
  // Schließe die Detailansicht
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedItem(null);
  };

  // Öffne die Rohdaten-Ansicht
  const openRawData = (data, event) => {
    event.stopPropagation();
    setCurrentRawData(data);
    setRawDataModalOpen(true);
  };
  
  // Schließe die Rohdaten-Ansicht
  const closeRawData = () => {
    setRawDataModalOpen(false);
    setCurrentRawData('');
  };

  // Extrahiere KB-Artikel und URLs aus den Nachrichten
  const extractReferences = (messages: Message[] | null | undefined, rawText?: string | null): Reference[] => {
    const references: Reference[] = [];
    
    // Patterns für KB-Artikel und URLs
    const kbPattern = /(KB\s+(\d+)|KB article\s+(\d+)|KB\s+[a-zA-Z0-9]+|KB\s+article[s]?\s+[a-zA-Z0-9]+)/gi;
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    
    // Aus strukturierten Nachrichten extrahieren
    if (messages && messages.length > 0) {
      messages.forEach(msg => {
        if (!msg.message) return;
        
        // KB-Artikel finden
        const kbMatches = msg.message.match(kbPattern);
        if (kbMatches) {
          kbMatches.forEach(match => {
            if (!references.some(ref => ref.text === match)) {
              references.push({ text: match, isUrl: false });
            }
          });
        }
        
        // URLs finden
        const urlMatches = msg.message.match(urlPattern);
        if (urlMatches) {
          urlMatches.forEach(match => {
            if (!references.some(ref => ref.text === match)) {
              references.push({ text: match, isUrl: true });
            }
          });
        }
      });
    }
    
    // Aus Rohdaten extrahieren (falls vorhanden)
    if (rawText) {
      // KB-Artikel finden
      const kbMatches = rawText.match(kbPattern);
      if (kbMatches) {
        kbMatches.forEach(match => {
          if (!references.some(ref => ref.text === match)) {
            references.push({ text: match, isUrl: false });
          }
        });
      }
      
      // URLs finden
      const urlMatches = rawText.match(urlPattern);
      if (urlMatches) {
        urlMatches.forEach(match => {
          if (!references.some(ref => ref.text === match)) {
            references.push({ text: match, isUrl: true });
          }
        });
      }
    }
    
    return references;
  };
  
  // Formatiere Referenz-Label für bessere Lesbarkeit
  const formatReferenceLabel = (url) => {
    // Für KB-Artikel-URLs
    if (url.includes('kbdoc')) {
      const kbMatch = url.match(/kbdoc\/(\d+)/);
      if (kbMatch) {
        return `KB Artikel ${kbMatch[1]}`;
      }
    }
    
    // Für andere URLs - zeige Domain und Pfad
    try {
      const urlObj = new URL(url);
      return `${urlObj.hostname}${urlObj.pathname}`;
    } catch (e) {
      return url;
    }
  };

  // Funktion zum Gruppieren der Node Types
  const prepareNodeTypesData = (nodeTypes: Record<string, number>) => {
    return Object.entries(nodeTypes)
      .map(([type, count]) => ({ name: type, value: count }))
      .sort((a, b) => b.value - a.value);
  };

  // Generiere Statistiken
  const generateStatistics = (): StatsData | null => {
    try {
      // Leere Statistik zurückgeben, wenn keine Analysen vorhanden
      if (!savedAnalyses || savedAnalyses.length === 0) {
        return null;
      }
      
      const stats: StatsData = {
        clusterCount: savedAnalyses.length,
        totalChecks: 0,
        totalPassChecks: 0,
        totalWarnChecks: 0,
        totalFailChecks: 0,
        totalInfoChecks: 0,
        healthDistribution: [0, 0, 0], // healthy, warning, critical
        onefsVersions: {},
        iocaVersions: {},
        commonWarnings: {},
        commonFailures: {},
        commonInfos: {},
        nodeTypes: {},
        srsEnabled: 0,
        srsDisabled: 0,
        versionsData: [],
        iocaVersionsData: [],
        nodeTypesData: [],
        warningsData: [],
        failuresData: []
      };

      // Funktion zum Prüfen, ob ein Name ein UI-Symbol ist
      const isUiSymbol = (name: string): boolean => {
        return name.includes('×') || name.includes('✗') || name.trim() === '×' || name.trim() === '✗';
      };

      // Funktion zum Prüfen, ob ein Check ignoriert werden soll
      const shouldIgnoreCheck = (name: string): boolean => {
        const ignoredChecks = [
          'Critical Events',
          'Cluster Health Status',
          'Destination OneFS Version',
          'Supportability'
        ];
        return ignoredChecks.includes(name.trim());
      };
      
      // Durchlaufe alle Analysen
      savedAnalyses.forEach(analysis => {
        if (!analysis || !analysis.results) return;
        
        // SRS Status prüfen
        const rawData = analysis.rawData || '';
        if (rawData.includes('SRS (isi esrs) is enabled') || 
            rawData.includes('SRS is enabled') || 
            (rawData.includes('DialHome & Remote Connectivity') && 
             rawData.includes('DETAILS: SRS') && 
             !rawData.includes('is not enabled'))) {
          stats.srsEnabled++;
        } else {
          stats.srsDisabled++;
        }
        
        // Gesundheitsstatus
        const health = getHealthStatus(analysis.results);
        if (health === 'healthy') stats.healthDistribution[0]++;
        else if (health === 'warning') stats.healthDistribution[1]++;
        else if (health === 'critical') stats.healthDistribution[2]++;
        
        // OneFS Version
        if (analysis.results.clusterInfo && analysis.results.clusterInfo.currentVersion) {
          const version = analysis.results.clusterInfo.currentVersion;
          stats.onefsVersions[version] = (stats.onefsVersions[version] || 0) + 1;
        }

        // IOCA Version
        if (analysis.results.clusterInfo && analysis.results.clusterInfo.iocaVersion) {
          const version = analysis.results.clusterInfo.iocaVersion;
          stats.iocaVersions[version] = (stats.iocaVersions[version] || 0) + 1;
        }

        // Node Types
        if (analysis.results.clusterInfo && analysis.results.clusterInfo.nodeTypes) {
          analysis.results.clusterInfo.nodeTypes.forEach(nodeType => {
            // Entferne mögliche Whitespaces und normalisiere den Node Type
            const normalizedType = nodeType.trim();
            if (normalizedType) {
              stats.nodeTypes[normalizedType] = (stats.nodeTypes[normalizedType] || 0) + 1;
            }
          });
        }
        
        // Zähle Checks (ohne UI-Symbole und ignorierte Checks)
        if (analysis.results.categories) {
          const categories = analysis.results.categories;
          
          // PASS Checks
          if (Array.isArray(categories.PASS)) {
            stats.totalPassChecks += categories.PASS.filter(item => 
              !isUiSymbol(item.name) && !shouldIgnoreCheck(item.name)
            ).length;
          }
          
          // WARN Checks
          if (Array.isArray(categories.WARN)) {
            const realWarnings = categories.WARN.filter(item => 
              !isUiSymbol(item.name) && !shouldIgnoreCheck(item.name)
            );
            stats.totalWarnChecks += realWarnings.length;
            
            // Sammle echte Warnungen
            realWarnings.forEach(item => {
              if (item && item.name) {
                stats.commonWarnings[item.name] = (stats.commonWarnings[item.name] || 0) + 1;
              }
            });
          }
          
          // FAIL Checks
          if (Array.isArray(categories.FAIL)) {
            const realFailures = categories.FAIL.filter(item => 
              !isUiSymbol(item.name) && !shouldIgnoreCheck(item.name)
            );
            stats.totalFailChecks += realFailures.length;
            
            // Sammle echte Fehler
            realFailures.forEach(item => {
              if (item && item.name) {
                stats.commonFailures[item.name] = (stats.commonFailures[item.name] || 0) + 1;
              }
            });
          }
          
          // INFO Checks
          if (Array.isArray(categories.INFO)) {
            const realInfos = categories.INFO.filter(item => 
              !isUiSymbol(item.name) && !shouldIgnoreCheck(item.name)
            );
            stats.totalInfoChecks += realInfos.length;
            
            // Sammle echte Infos
            realInfos.forEach(item => {
              if (item && item.name) {
                stats.commonInfos[item.name] = (stats.commonInfos[item.name] || 0) + 1;
              }
            });
          }
        }
      });
      
      // Berechne Gesamtzahl der Checks (ohne UI-Symbole)
      stats.totalChecks = stats.totalPassChecks + stats.totalWarnChecks + 
                         stats.totalFailChecks + stats.totalInfoChecks;
      
      // Transformiere die Objekte in Arrays für die Charts
      stats.versionsData = Object.entries(stats.onefsVersions).map(([version, count]) => ({
        name: version,
        value: count
      }));
      
      stats.iocaVersionsData = Object.entries(stats.iocaVersions).map(([version, count]) => ({
        name: version,
        value: count
      }));
      
      // Transformiere die Node Types Daten
      stats.nodeTypesData = prepareNodeTypesData(stats.nodeTypes);
      
      // Sortiere und konvertiere Warnungen und Fehler
      type CountEntry = { name: string; count: number };
      
      stats.warningsData = Object.entries(stats.commonWarnings)
        .map(([name, count]): CountEntry => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      
      stats.failuresData = Object.entries(stats.commonFailures)
        .map(([name, count]): CountEntry => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      
      return stats;
    } catch (error) {
      console.error("Error generating statistics:", error);
      return null;
    }
  };

  const getNodeTypeColor = (nodeType: string): string => {
    if (nodeType.startsWith('X')) return '#4299e1';      // X-Series: Blau
    if (nodeType.startsWith('H')) return '#48bb78';      // H-Series: Grün
    if (nodeType.startsWith('A')) return '#ed8936';      // A-Series: Orange
    if (nodeType.startsWith('F')) return '#667eea';      // F-Series: Indigo
    if (nodeType.startsWith('NL')) return '#9f7aea';     // NL-Series: Violett
    if (nodeType.startsWith('P')) return '#ed64a6';      // P-Series: Pink
    if (nodeType.startsWith('B')) return '#718096';      // B-Series: Grau
    return '#a0aec0';                                    // Fallback: Grau
  };

  const getNodeTypeSeries = (nodeType: string): string => {
    if (nodeType.startsWith('X')) return 'X-Series';
    if (nodeType.startsWith('H')) return 'H-Series';
    if (nodeType.startsWith('A')) return 'A-Series';
    if (nodeType.startsWith('F')) return 'F-Series';
    if (nodeType.startsWith('NL')) return 'NL-Series';
    if (nodeType.startsWith('P')) return 'P-Series';
    if (nodeType.startsWith('B')) return 'B-Series';
    return 'Other';
  };

  // Load saved analyses from localStorage on component mount
  useEffect(() => {
    loadAnalysesFromFile();
  }, []);

  // Aktualisiere Statistiken, wenn sich savedAnalyses ändert
  useEffect(() => {
    try {
      if (savedAnalyses.length > 0) {
        const stats = generateStatistics();
        setStatsData(stats);
      } else {
        setStatsData(null);
      }
    } catch (error) {
      console.error("Error updating statistics:", error);
    }
  }, [savedAnalyses]);

  // Funktion zum Laden der Analysen aus der JSON-Datei
  const loadAnalysesFromFile = async () => {
    try {
      const response = await fetch('saved_analyses.json');
      if (response.ok) {
        const analyses = await response.json();
        setSavedAnalyses(analyses);
      }
    } catch (e) {
      console.error("Error loading saved analyses:", e);
      setSavedAnalyses([]);
    }
  };

  // Funktion zum Speichern der Analysen in eine JSON-Datei
  const saveAnalysesToFile = (analyses) => {
    const jsonString = JSON.stringify(analyses, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'saved_analyses.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Datei-Auswahl Handler
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  // Funktion zum Laden einer gespeicherten JSON-Datei
  const handleSavedFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const text = await readFileAsText(file);
        const analyses = JSON.parse(text);
        setSavedAnalyses(analyses);
      } catch (e) {
        console.error("Error loading saved analyses file:", e);
        alert("Error loading saved analyses file. Please check the file format.");
      }
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === 'string') {
          resolve(event.target.result);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const analyzeLog = async () => {
    if (!file) {
      alert("Bitte wählen Sie eine Datei aus.");
      return;
    }
    
    setLoading(true);
    
    try {
      const text = await readFileAsText(file);
      
      if (!text || text.trim().length === 0) {
        throw new Error("Die Datei ist leer.");
      }
      
      setLogData(text);
      
      // Prüfe ob es sich um eine IOCA Log-Datei handelt
      if (!text.includes("Isilon On-Cluster Analysis") && 
          !text.includes("Live Cluster Analysis") &&
          !text.includes("Cluster Name:") &&
          !text.includes("Node Count:")) {
        throw new Error("Die Datei scheint keine gültige IOCA Log-Datei zu sein.");
      }
      
      const results = parseIsilonLog(text);
      
      if (!results) {
        throw new Error("Die Log-Datei konnte nicht analysiert werden.");
      }
      
      // Validiere die Ergebnisse
      if (!results.categories || 
          (!results.categories.PASS.length && 
           !results.categories.WARN.length && 
           !results.categories.FAIL.length && 
           !results.categories.INFO.length)) {
        throw new Error("Die Log-Datei enthält keine analysierbaren Daten.");
      }
      
      results.displayName = analysisName.trim() || file.name;
      setAnalysisResults(results);
      
      // Save the analysis
      const newAnalysis = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        filename: file.name,
        displayName: results.displayName,
        results: results,
        rawData: text
      };
      
      const updatedAnalyses = [newAnalysis, ...savedAnalyses].slice(0, 10);
      setSavedAnalyses(updatedAnalyses);
      saveAnalysesToFile(updatedAnalyses);
      
      setActiveTab('results');
    } catch (error) {
      console.error("Error analyzing log:", error);
      alert(error instanceof Error ? error.message : "Fehler beim Analysieren der Logdatei.");
    } finally {
      setLoading(false);
    }
  };

  const loadSavedAnalysis = (analysisId) => {
    const analysis = savedAnalyses.find(a => a.id === analysisId);
    if (analysis) {
      setLogData(analysis.rawData);
      const results = analysis.results;
      
      // Stelle sicher, dass der Anzeigename übernommen wird
      results.displayName = analysis.displayName;
      
      setAnalysisResults(results);
      setActiveTab('results');
    }
  };

  const deleteAnalysis = (analysisId, event) => {
    event.stopPropagation();
    const updatedAnalyses = savedAnalyses.filter(a => a.id !== analysisId);
    setSavedAnalyses(updatedAnalyses);
    saveAnalysesToFile(updatedAnalyses);
  };

  // Funktion zum Bestimmen des Gesundheitsstatus
  const getHealthStatus = (results) => {
    if (!results) return 'unknown';
    
    const counts = countResults(results);
    
    if (counts.FAIL > 0) return 'critical';
    if (counts.WARN > 0) return 'warning';
    return 'healthy';
  };

  // Funktion zum Bestimmen der Farbe für den Gesundheitsstatus
  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'critical': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'healthy': return '#28a745';
      default: return '#6c757d';
    }
  };

  // Funktion zum Bestimmen des Textes für den Gesundheitsstatus
  const getHealthStatusText = (status) => {
    switch (status) {
      case 'critical': return 'Critical';
      case 'warning': return 'Warning';
      case 'healthy': return 'Healthy';
      default: return 'Unknown';
    }
  };

  // Hilfsfunktion zum Formatieren von Links im Text
  const formatLinksInText = (text) => {
    if (!text) return '';
    
    // Regulärer Ausdruck für KB-Artikel-Referenzen
    const kbPattern = /(KB\s+(\d+)|KB article\s+(\d+)|KB\s+[a-zA-Z0-9]+|KB\s+article[s]?\s+[a-zA-Z0-9]+)/gi;
    
    // Regulärer Ausdruck für URLs
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    
    // Ersetze KB-Artikel-Referenzen
    let formattedText = text.replace(kbPattern, '<span style="font-weight: bold; color: #1e40af;">$1</span>');
    
    // Ersetze URLs mit klickbaren Links
    formattedText = formattedText.replace(urlPattern, '<a href="$1" target="_blank" style="color: #2563eb; text-decoration: underline;">$1</a>');
    
    return formattedText;
  };

  // Modal für Rohdaten-Anzeige
  const RawDataModal = ({ data, onClose }) => {
    if (!data) return null;
    
    const copyToClipboard = () => {
      navigator.clipboard.writeText(data).then(() => {
        alert("Raw data copied to clipboard!");
      }, (err) => {
        console.error('Error copying:', err);
      });
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl h-[80vh] flex flex-col">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-10">
            <h2 className="text-xl font-bold">IOCA Log Raw Data</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4" style={{ height: 'calc(80vh - 130px)' }}>
            <pre className="whitespace-pre-wrap text-sm font-mono bg-gray-50 p-4 rounded h-full">{data}</pre>
          </div>
          
          <div className="bg-gray-50 p-4 border-t flex justify-between">
            <button 
              onClick={copyToClipboard}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
            >
              Copy to Clipboard
            </button>
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // JSON Viewer Komponente
  const JSONViewer = ({ data }) => {
    const [expandedKeys, setExpandedKeys] = useState(new Set());

    const toggleExpand = (key) => {
      const newExpanded = new Set(expandedKeys);
      if (newExpanded.has(key)) {
        newExpanded.delete(key);
      } else {
        newExpanded.add(key);
      }
      setExpandedKeys(newExpanded);
    };

    const renderValue = (value, depth = 0, path = '') => {
      if (value === null) return <span className="text-gray-500">null</span>;
      if (value === undefined) return <span className="text-gray-500">undefined</span>;
      if (typeof value === 'boolean') {
        return <span className={value ? 'text-green-600' : 'text-red-600'}>{value.toString()}</span>;
      }
      if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
      if (typeof value === 'string') return <span className="text-gray-800">"{value}"</span>;
      
      if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-gray-500">[]</span>;
        const currentPath = `${path}[]`;
        const isExpanded = expandedKeys.has(currentPath);
        
        return (
          <div className="ml-4">
            <div 
              className="flex items-center cursor-pointer hover:bg-gray-50 -ml-4 px-4"
              onClick={() => toggleExpand(currentPath)}
            >
              <span className="text-gray-500 mr-2">{isExpanded ? '▼' : '▶'}</span>
              <span className="text-gray-600">[{value.length} items]</span>
            </div>
            {isExpanded && (
              <div className="ml-4">
                {value.map((item, index) => (
                  <div key={index} className="my-1">
                    {renderValue(item, depth + 1, `${currentPath}[${index}]`)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      
      if (typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 0) return <span className="text-gray-500">{}</span>;
        
        return (
          <div className="ml-4">
            {entries.map(([key, val], index) => {
              const currentPath = `${path}.${key}`;
              const isExpandable = typeof val === 'object' && val !== null;
              const isExpanded = expandedKeys.has(currentPath);
              
              return (
                <div key={key} className="my-1">
                  <div 
                    className={`flex items-start ${isExpandable ? 'cursor-pointer hover:bg-gray-50 -ml-4 px-4' : ''}`}
                    onClick={isExpandable ? () => toggleExpand(currentPath) : undefined}
                  >
                    {isExpandable && (
                      <span className="text-gray-500 mr-2 mt-1">{isExpanded ? '▼' : '▶'}</span>
                    )}
                    <div className="flex-1">
                      <span className="font-medium text-gray-700">{key}:</span>{' '}
                      {isExpandable ? (
                        isExpanded ? (
                          <div className="mt-1">{renderValue(val, depth + 1, currentPath)}</div>
                        ) : (
                          <span className="text-gray-600">
                            {Array.isArray(val) ? `[${val.length} items]` : '{...}'}
                          </span>
                        )
                      ) : (
                        renderValue(val, depth + 1, currentPath)
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      
      return <span className="text-gray-800">{String(value)}</span>;
    };

    return (
      <div className="font-mono text-sm">
        {renderValue(data)}
      </div>
    );
  };

  // Detailansicht-Komponente
  const DetailModal = ({ item, onClose }) => {
    if (!item) return null;

    const [expandedSections, setExpandedSections] = useState({
      logDetails: true,
      structuredDetails: true,
      references: true
    });

    const toggleSection = (section: string) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    const SectionHeader = ({ title, section, isExpanded }: { title: string; section: string; isExpanded: boolean }) => (
      <div
        onClick={() => toggleSection(section)}
        className="flex items-center justify-between cursor-pointer py-2 hover:bg-gray-50"
      >
        <h3 className="font-bold text-gray-700">{title}</h3>
        <span className="text-gray-500">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
    );
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center rounded-t-lg">
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold">{item.name}</h2>
              <div className={`px-3 py-1 rounded text-white text-sm font-bold ${
                item.status === 'FAIL' ? 'bg-red-500' : 
                item.status === 'WARN' ? 'bg-yellow-500' : 
                item.status === 'INFO' ? 'bg-blue-500' : 'bg-green-500'
              }`}>
                {item.status}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Log Details Section */}
            {item.rawDetails && (
              <div className="mb-4">
                <SectionHeader 
                  title="Log Details" 
                  section="logDetails"
                  isExpanded={expandedSections.logDetails}
                />
                {expandedSections.logDetails && (
                  <div className="mt-2 bg-gray-50 rounded border border-gray-200 p-3">
                    <JSONViewer data={JSON.parse(item.rawDetails)} />
                  </div>
                )}
              </div>
            )}
            
            {/* Structured Details Section */}
            {item.messages && item.messages.length > 0 && (
              <div className="mb-4">
                <SectionHeader 
                  title="Structured Details" 
                  section="structuredDetails"
                  isExpanded={expandedSections.structuredDetails}
                />
                {expandedSections.structuredDetails && (
                  <div className="mt-2">
                    <JSONViewer data={item.messages} />
                  </div>
                )}
              </div>
            )}
            
            {/* References Section */}
            <div className="mb-4">
              <SectionHeader 
                title="References" 
                section="references"
                isExpanded={expandedSections.references}
              />
              {expandedSections.references && (
                <div className="mt-2 bg-gray-50 rounded border border-gray-200 p-3">
                  {(() => {
                    const allReferences = extractReferences(item.messages, item.rawDetails);
                    return allReferences.length > 0 ? (
                      <ul className="list-disc pl-4 space-y-1 text-sm">
                        {allReferences.map((ref, idx) => (
                          <li key={idx} className="text-gray-700">
                            {ref.isUrl ? (
                              <a 
                                href={ref.text} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {formatReferenceLabel(ref.text)}
                              </a>
                            ) : (
                              <span className="font-medium">{ref.text}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No references found.</p>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 p-3 border-t rounded-b-lg flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUploadTab = () => (
    <div className="p-4 bg-white rounded shadow">
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="file-upload">
          Select Isilon Analysis File
        </label>
        <input
          id="file-upload"
          type="file"
          onChange={handleFileChange}
          className="w-full p-2 border border-gray-300 rounded"
          accept=".txt,.log"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="analysis-name">
          Description (optional)
        </label>
        <input
          id="analysis-name"
          type="text"
          value={analysisName}
          onChange={(e) => setAnalysisName(e.target.value)}
          placeholder="e.g. Description or Note"
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>
      
      <button
        onClick={analyzeLog}
        disabled={!file || loading}
        className={`w-full p-2 text-white rounded font-bold ${!file || loading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-700'}`}
      >
        {loading ? 'Analyzing...' : 'Analyze File'}
      </button>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="p-4 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Saved Analyses</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => saveAnalysesToFile(savedAnalyses)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-32 h-10 flex items-center justify-center text-base"
          >
            Export
          </button>
          <label className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 cursor-pointer w-32 h-10 flex items-center justify-center text-base">
            Import
            <input
              type="file"
              accept=".json"
              onChange={handleSavedFileUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>
      
      {savedAnalyses.length === 0 ? (
        <p>No saved analyses available.</p>
      ) : (
        <div className="space-y-2">
          {savedAnalyses.map((analysis) => (
            <div
              key={analysis.id}
              className="p-3 border rounded hover:bg-gray-100"
            >
              <div className="flex justify-between items-center cursor-pointer" onClick={() => loadSavedAnalysis(analysis.id)}>
                <div>
                  <div className="font-medium">{analysis.displayName || analysis.filename}</div>
                  <div className="text-sm text-gray-500">{analysis.date}</div>
                  <div className="text-sm">
                    Cluster: {analysis.results.clusterInfo.name} | 
                    Version: {analysis.results.clusterInfo.currentVersion} → {analysis.results.clusterInfo.targetVersion}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: getHealthStatusColor(getHealthStatus(analysis.results)) }}
                    title={getHealthStatusText(getHealthStatus(analysis.results))}
                  ></div>
                </div>
              </div>
              
              {/* Aktionsbuttons */}
              <div className="mt-2 pt-2 border-t flex justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentRawData(analysis.rawData);
                    setRawDataModalOpen(true);
                  }}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                >
                  View Raw Data
                </button>
                <button
                  onClick={(e) => deleteAnalysis(analysis.id, e)}
                  className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Rohdaten Modal direkt im History Tab für bessere Sichtbarkeit */}
      {rawDataModalOpen && (
        <RawDataModal
          data={currentRawData}
          onClose={() => setRawDataModalOpen(false)}
        />
      )}
    </div>
  );

  const renderResultsTab = () => {
    if (!analysisResults) {
      return (
        <div className="p-4 bg-white rounded shadow">
          <p>No analysis results available. Please upload a file and analyze it.</p>
        </div>
      );
    }

    const { clusterInfo, categories } = analysisResults;
    const counts = countResults(analysisResults);
    const healthStatus = getHealthStatus(analysisResults);
    
    // Verwende die neue Funktion zum Zählen der Ergebnisse
    const recalculatedCounts = countResults(analysisResults);
    //
    // Prepare data for charts
    const countData = [
      { name: 'FAIL', value: recalculatedCounts.FAIL, color: '#dc3545' },
      { name: 'WARN', value: recalculatedCounts.WARN, color: '#ffc107' },
      { name: 'INFO', value: recalculatedCounts.INFO, color: '#17a2b8' },
      { name: 'PASS', value: recalculatedCounts.PASS, color: '#28a745' }
    ];

    return (
      <div className="space-y-6">
        {/* Cluster Info */}
        <div className="p-4 bg-white rounded shadow">
          <div className="mb-4">
            <h2 className="text-xl font-bold">Cluster Information</h2>
          </div>
          
          {/* Kundennamen anzeigen, falls vorhanden */}
          {analysisResults.displayName && (
            <div className="mb-4 p-2 bg-gray-100 rounded">
              <p className="font-semibold">Analysis: {analysisResults.displayName}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p><strong>IOCA Version:</strong> {clusterInfo.iocaVersion}</p>
              <p><strong>Cluster Name:</strong> {clusterInfo.name}</p>
              <p><strong>Cluster GUID:</strong> {clusterInfo.clusterGuid}</p>
              <p><strong>Number of Nodes:</strong> {clusterInfo.nodeCount}</p>
              <p><strong>Current OneFS Version:</strong> {clusterInfo.currentVersion}</p>
              <p><strong>Destination OneFS Version:</strong> {clusterInfo.targetVersion}</p>
              <p>
                <strong>Support Assist Status:</strong>{' '}
                <span className={logData.includes('SRS (isi esrs) is enabled') || 
                               logData.includes('SRS is enabled') || 
                               (logData.includes('DialHome & Remote Connectivity') && 
                                logData.includes('DETAILS: SRS') && 
                                !logData.includes('is not enabled')) ? 
                               'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                  {logData.includes('SRS (isi esrs) is enabled') || 
                   logData.includes('SRS is enabled') || 
                   (logData.includes('DialHome & Remote Connectivity') && 
                    logData.includes('DETAILS: SRS') && 
                    !logData.includes('is not enabled')) ? 'Enabled' : 'Disabled'}
                </span>
              </p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <strong className="mr-2">Overall Status:</strong>
                <span 
                  className="inline-block px-3 py-1 rounded text-white font-bold"
                  style={{ backgroundColor: getHealthStatusColor(healthStatus) }}
                >
                  {getHealthStatusText(healthStatus)}
                </span>
              </div>
              <p><strong>Metrics:</strong></p>
              <ul className="list-disc pl-5">
                <li className="text-red-600">Errors: {recalculatedCounts.FAIL}</li>
                <li className="text-yellow-600">Warnings: {recalculatedCounts.WARN}</li>
                <li className="text-blue-600">Info: {recalculatedCounts.INFO}</li>
                <li className="text-green-600">Success: {recalculatedCounts.PASS}</li>
                <li>Total: {recalculatedCounts.total}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="p-4 bg-white rounded shadow">
          <h2 className="text-xl font-bold mb-4">Results Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Count">
                    {countData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={countData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {countData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Upgrade Plan if available */}
        {clusterInfo.upgradePlan && (
          <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-2">Upgrade Plan</h2>
            
            {counts.FAIL > 0 && (
              <div className="mb-3 p-3 bg-red-50 rounded border border-red-300">
                <p className="font-semibold text-red-700">
                  <span className="mr-2">⚠️</span>
                  Warning: {counts.FAIL} errors were found. The upgrade cannot be completed successfully until these issues are resolved.
                </p>
              </div>
            )}
            
            <div className="p-2 bg-gray-100 rounded">
              <pre className="whitespace-pre-wrap">{clusterInfo.upgradePlan}</pre>
            </div>
            
            {clusterInfo.estimatedDuration && (
              <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                <p className="font-semibold text-blue-700">Estimated total duration: {clusterInfo.estimatedDuration}</p>
              </div>
            )}
          </div>
        )}

        {/* Failures */}
        {categories.FAIL.length > 0 && (
          <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-4">
              <span className="text-red-600">Errors</span>
              <span className="text-gray-700"> ({categories.FAIL.length})</span>
            </h2>
            <div className="space-y-4">
              {categories.FAIL.map((check, index) => (
                <div 
                  key={`fail-${index}`} 
                  className="p-3 border border-red-300 rounded bg-red-50 cursor-pointer hover:bg-red-100"
                  onClick={() => openDetails(check)}
                >
                  <div className="font-bold text-red-700">{check.name}</div>
                  {check.messages && check.messages.length > 0 && (
                    <ul className="mt-2 list-disc pl-5">
                      <li className="text-red-700">
                        {check.messages[0].message}
                        {check.messages.length > 1 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (+ {check.messages.length - 1} more)
                          </span>
                        )}
                      </li>
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {categories.WARN.length > 0 && (
          <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-4 text-yellow-600">Warnings ({categories.WARN.length})</h2>
            <div className="space-y-4">
              {categories.WARN.map((check, index) => (
                <div 
                  key={`warn-${index}`} 
                  className="p-3 border border-yellow-300 rounded bg-yellow-50 cursor-pointer hover:bg-yellow-100"
                  onClick={() => openDetails(check)}
                >
                  <div className="font-bold">{check.name}</div>
                  {check.additionalInfo && <div className="text-sm italic">{check.additionalInfo}</div>}
                  <ul className="mt-2 list-disc pl-5">
                    {(check.messages && check.messages.length > 0) ? check.messages.slice(0, 1).map((msg, msgIndex) => (
                      <li key={`warn-msg-${index}-${msgIndex}`} className="text-yellow-700">
                        {msg.message}
                        {check.messages.length > 1 && msgIndex === 0 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (+ {check.messages.length - 1} more details...)
                          </span>
                        )}
                      </li>
                    )) : (
                      <li className="text-gray-500">Click for details</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INFO */}
        {categories.INFO.length > 0 && (
          <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-4 text-blue-600">Information ({categories.INFO.length})</h2>
            <div className="space-y-4">
              {categories.INFO.map((check, index) => (
                <div 
                  key={`info-${index}`} 
                  className="p-3 border border-blue-300 rounded bg-blue-50 cursor-pointer hover:bg-blue-100"
                  onClick={() => openDetails(check)}
                >
                  <div className="font-bold">{check.name}</div>
                  {check.additionalInfo && <div className="text-sm italic">{check.additionalInfo}</div>}
                  <ul className="mt-2 list-disc pl-5">
                    {(check.messages && check.messages.length > 0) ? check.messages.slice(0, 1).map((msg, msgIndex) => (
                      <li key={`info-msg-${index}-${msgIndex}`} className="text-blue-700">
                        {msg.message}
                        {check.messages.length > 1 && msgIndex === 0 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (+ {check.messages.length - 1} more details...)
                          </span>
                        )}
                      </li>
                    )) : (
                      <li className="text-gray-500">Click for details</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PASS - Collapsible */}
        {categories.PASS.length > 0 && (
          <div className="p-4 bg-white rounded shadow">
            <details>
              <summary className="text-xl font-bold mb-4 text-green-600 cursor-pointer">
                Successful Tests ({categories.PASS.length})
              </summary>
              <div className="space-y-4 mt-4">
                {categories.PASS.map((check, index) => (
                  <div 
                    key={`pass-${index}`} 
                    className="p-3 border border-green-300 rounded bg-green-50 cursor-pointer hover:bg-green-100"
                    onClick={() => openDetails(check)}
                  >
                    <div className="font-bold">{check.name}</div>
                    {check.additionalInfo && <div className="text-sm italic">{check.additionalInfo}</div>}
                    {(check.messages && check.messages.length > 0) && (
                      <div className="mt-2 text-sm text-gray-500">
                        {check.messages.length} detail(s) available - Click to view
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  const renderNodeTypesDistribution = () => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-4">Node Types Distribution</h3>
      {statsData && statsData.nodeTypesData && statsData.nodeTypesData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={statsData.nodeTypesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" name="Count">
                {statsData.nodeTypesData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getNodeTypeColor(entry.name)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-gray-500">No node types found.</p>
      )}
    </div>
  );

  const renderNodeTypesReference = () => (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-4">Isilon/PowerScale Node Types Reference</h3>
      <div className="space-y-4">
        {/* Erste Zeile: X, H, A, F Series */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {['X-Series', 'H-Series', 'A-Series', 'F-Series'].map(series => (
            <div key={series} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium">{series}</div>
                <button
                  onClick={() => {
                    const newType = prompt(`Add new ${series} node type:`);
                    if (newType && /^[XHAF]\d{3,4}$/.test(newType)) {
                      updateNodeTypes(series, [...(customNodeTypes[series] || []), newType]);
                    } else if (newType) {
                      alert(`Invalid node type format for ${series}`);
                    }
                  }}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  +
                </button>
              </div>
              <div className="space-y-1">
                {(customNodeTypes[series] || []).map((type: string) => (
                  <div key={type} className="flex justify-between items-center text-sm">
                    <span>{type}</span>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${type}?`)) {
                          updateNodeTypes(series, (customNodeTypes[series] || []).filter(t => t !== type));
                        }
                      }}
                      className="text-red-500 hover:text-red-700 text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Zweite Zeile: NL, P, B Series */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {['NL-Series', 'P-Series', 'B-Series'].map(series => (
            <div key={series} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium">{series}</div>
                <button
                  onClick={() => {
                    const newType = prompt(`Add new ${series} node type:`);
                    if (newType) {
                      let isValid = false;
                      if (series === 'NL-Series' && /^NL\d{3,4}$/.test(newType)) isValid = true;
                      else if (series === 'P-Series' && /^P\d{3,4}$/.test(newType)) isValid = true;
                      else if (series === 'B-Series' && /^B\d{3,4}$/.test(newType)) isValid = true;

                      if (isValid) {
                        updateNodeTypes(series, [...(customNodeTypes[series] || []), newType]);
                      } else {
                        alert(`Invalid node type format for ${series}`);
                      }
                    }
                  }}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  +
                </button>
              </div>
              <div className="space-y-1">
                {(customNodeTypes[series] || []).map((type: string) => (
                  <div key={type} className="flex justify-between items-center text-sm">
                    <span>{type}</span>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${type}?`)) {
                          updateNodeTypes(series, (customNodeTypes[series] || []).filter(t => t !== type));
                        }
                      }}
                      className="text-red-500 hover:text-red-700 text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 text-sm text-gray-500">
        <p>Click + to add new node types. Click × to remove existing ones.</p>
        <p>Node type formats:</p>
        <ul className="list-disc ml-5 mt-1">
          <li>X/H/A/F-Series: Letter followed by 3-4 digits (e.g., X400, H700)</li>
          <li>NL-Series: NL followed by 3-4 digits (e.g., NL400)</li>
          <li>P-Series: P followed by 3-4 digits (e.g., P100)</li>
          <li>B-Series: B followed by 3-4 digits (e.g., B100)</li>
        </ul>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* Node Types Reference */}
      {renderNodeTypesReference()}
    </div>
  );

  const renderStatisticsTab = () => {
    if (!statsData || savedAnalyses.length === 0) {
      return (
        <div className="p-4 bg-white rounded shadow">
          <p>No statistics available. Please analyze at least one log file.</p>
        </div>
      );
    }

    // Daten für die Health Distribution
    const healthData = [
      { name: 'Healthy', value: statsData.healthDistribution[0], color: '#28a745' },
      { name: 'Warning', value: statsData.healthDistribution[1], color: '#ffc107' },
      { name: 'Critical', value: statsData.healthDistribution[2], color: '#dc3545' }
    ];

    // Daten für die Test Results Distribution
    const testResultsData = [
      { name: 'Pass', value: statsData.totalPassChecks, color: '#28a745' },
      { name: 'Warning', value: statsData.totalWarnChecks, color: '#ffc107' },
      { name: 'Error', value: statsData.totalFailChecks, color: '#dc3545' },
      { name: 'Info', value: statsData.totalInfoChecks, color: '#17a2b8' }
    ];

    return (
      <div className="space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Total Clusters</div>
            <div className="text-2xl font-bold">{statsData.clusterCount}</div>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Total Tests</div>
            <div className="text-2xl font-bold">{statsData.totalChecks}</div>
            <div className="text-sm text-gray-500 mt-1">
              Avg. {Math.round(statsData.totalChecks / statsData.clusterCount)} per cluster
            </div>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Success Rate</div>
            <div className="text-2xl font-bold">
              {Math.round(statsData.totalChecks > 0 ? (statsData.totalPassChecks/statsData.totalChecks*100) : 0)}%
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {statsData.totalPassChecks} successful tests
            </div>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="text-sm text-gray-500 mb-1">Support Assist Status</div>
            <div className="text-2xl font-bold text-blue-600">
              {Math.round((statsData.srsEnabled / statsData.clusterCount) * 100)}%
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {statsData.srsEnabled} of {statsData.clusterCount} enabled
            </div>
          </div>
        </div>

        {/* Support Assist Details Card */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Support Assist Connection Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Enabled: {statsData.srsEnabled} clusters</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Disabled: {statsData.srsDisabled} clusters</span>
            </div>
          </div>
        </div>

        {/* Health Distribution and Test Results */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Cluster Health Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={healthData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value, percent }) => 
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {healthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Test Results Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={testResultsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Count">
                    {testResultsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* OneFS Versions, IOCA Versions and Node Types */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">OneFS Version Distribution</h3>
            {statsData.versionsData && statsData.versionsData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statsData.versionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name="Clusters" fill="#4a5568" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-gray-500">No version information available.</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">IOCA Version Distribution</h3>
            {statsData.iocaVersionsData && statsData.iocaVersionsData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statsData.iocaVersionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name="Clusters" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-gray-500">No IOCA version information available.</p>
            )}
          </div>

          {renderNodeTypesDistribution()}
        </div>

        {/* Common Issues */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <span className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></span>
              Most Common Warnings
            </h3>
            {statsData.warningsData && statsData.warningsData.length > 0 ? (
              <div className="space-y-3">
                {statsData.warningsData.map((item, index) => (
                  <div key={index} className="relative">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-sm text-gray-500">
                        {item.count} ({Math.round(item.count / statsData.clusterCount * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full" 
                        style={{ width: `${(item.count / statsData.clusterCount) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No warnings found.</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
              Most Common Errors
            </h3>
            {statsData.failuresData && statsData.failuresData.length > 0 ? (
              <div className="space-y-3">
                {statsData.failuresData.map((item, index) => (
                  <div key={index} className="relative">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-sm text-gray-500">
                        {item.count} ({Math.round(item.count / statsData.clusterCount * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full" 
                        style={{ width: `${(item.count / statsData.clusterCount) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No errors found.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAboutTab = () => (
    <div className="p-4 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-4">About Isilon/Powerscale Analyzer</h2>
      <p className="mb-3">This application helps analyze Isilon/PowerScale IOCA or As-Built files to quickly identify issues and upgrade requirements.</p>
      <div className="mt-4 text-sm">
        <p className="mb-2"><strong>Version:</strong> 1.0.0 <span className="text-blue-500">beta</span></p>
        <p className="mb-2"><strong>Author:</strong> © 2025 Christopher Siebert</p>
        <p className="mb-2"><strong>Support:</strong> <a href="mailto:christopher.siebert@concat.de" className="text-blue-500 hover:underline">christopher.siebert@concat.de</a></p>
      </div>
    </div>
  );

  // Funktion zum Parsen der IOCA Log-Datei
  const parseIsilonLog = (text: string): AnalysisResults | null => {
    try {
      const results: AnalysisResults = {
        clusterInfo: {
          name: '',
          clusterGuid: '',
          nodeCount: '',
          currentVersion: '',
          targetVersion: '',
          iocaVersion: '',
          upgradePlan: '',
          estimatedDuration: '',
          nodeTypes: []
        },
        categories: {
          PASS: [],
          WARN: [],
          FAIL: [],
          INFO: []
        }
      };

      // Extrahiere Cluster-Informationen
      const lines = text.split('\n');
      let currentSection = '';
      let currentCheck: Check | null = null;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // IOCA Version
        if (line.includes('Isilon On-Cluster Analysis Version')) {
          const match = line.match(/Version\s+(\d+\.\d+\.\d+)/);
          if (match) {
            results.clusterInfo.iocaVersion = match[1];
          }
        }

        // Cluster Name
        if (line.startsWith('Cluster Name:')) {
          results.clusterInfo.name = line.split(':')[1].trim();
        }

        // Cluster GUID
        if (line.startsWith('Cluster GUID:')) {
          results.clusterInfo.clusterGuid = line.split(':')[1].trim();
        }

        // Node Count
        if (line.startsWith('Node Count:')) {
          results.clusterInfo.nodeCount = line.split(':')[1].trim();
        }

        // OneFS Versionen
        if (line.includes('Current OneFS Version:')) {
          results.clusterInfo.currentVersion = line.split(':')[1].trim();
        }
        if (line.includes('Destination OneFS Version:')) {
          results.clusterInfo.targetVersion = line.split(':')[1].trim();
        }

        // Node Types
        if (line.includes('The following node types are installed:')) {
          const nodeTypesMatch = line.match(/installed:\s+(.+?)\s+\(/);
          if (nodeTypesMatch) {
            const nodeType = nodeTypesMatch[1].trim();
            if (!results.clusterInfo.nodeTypes.includes(nodeType)) {
              results.clusterInfo.nodeTypes.push(nodeType);
            }
          }
        }

        // Upgrade Plan und geschätzte Dauer
        if (line.includes('Upgrade Plan:')) {
          const planLines: string[] = [];
          let j = i + 1;
          while (j < lines.length && !lines[j].trim().startsWith('Estimated total duration:')) {
            if (lines[j].trim()) planLines.push(lines[j].trim());
            j++;
          }
          results.clusterInfo.upgradePlan = planLines.join('\n');
          
          // Suche nach der geschätzten Dauer
          if (j < lines.length && lines[j].includes('Estimated total duration:')) {
            results.clusterInfo.estimatedDuration = lines[j].split(':')[1].trim();
          }
        }

        // Status-Kategorien
        if (line.match(/^(PASS|WARN|FAIL|INFO):/)) {
          const [status, name] = line.split(':').map(s => s.trim());
          currentCheck = {
            name,
            status,
            messages: [],
            rawDetails: '',
            additionalInfo: ''
          };
          
          const detailsLines: string[] = [];
          let j = i + 1;
          while (j < lines.length && 
                 !lines[j].trim().match(/^(PASS|WARN|FAIL|INFO):/) && 
                 lines[j].trim() !== '') {
            detailsLines.push(lines[j].trim());
            j++;
          }
          
          if (detailsLines.length > 0) {
            currentCheck.rawDetails = detailsLines.join('\n');
            
            // Parse strukturierte Nachrichten
            detailsLines.forEach((detail: string) => {
              if (detail.includes('CRITICAL:') || 
                  detail.includes('WARNING:') || 
                  detail.includes('INFO:')) {
                const [type, ...messageParts] = detail.split(':');
                currentCheck?.messages.push({
                  type: type.trim(),
                  message: messageParts.join(':').trim(),
                  rawLine: detail
                });
              }
            });
          }
          
          if (currentCheck) {
            results.categories[status as keyof typeof results.categories].push(currentCheck);
          }
          
          i = j - 1; // Setze den Index zurück
        }
      }

      return results;
    } catch (error) {
      console.error('Error parsing IOCA log:', error);
      return null;
    }
  };

  const renderGlobalTabs = () => {
    if (!globalTab) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold">{globalTab === 'settings' ? 'Settings' : 'About'}</h2>
            <button 
              onClick={() => setGlobalTab(null)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          
          <div className="p-4">
            {globalTab === 'settings' && renderSettingsTab()}
            {globalTab === 'about' && renderAboutTab()}
          </div>
        </div>
      </div>
    );
  };

  // Load saved documents on component mount
  useEffect(() => {
    loadDocsFromFile();
  }, []);

  const loadDocsFromFile = async () => {
    try {
      const response = await fetch('saved_docs.json');
      if (response.ok) {
        const docs = await response.json();
        setSavedDocs(docs);
      }
    } catch (e) {
      console.error("Error loading saved documents:", e);
      setSavedDocs([]);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Isilon/Powerscale Analyzer <span className="text-xs text-gray-500">v1.0.0 <sup className="text-xs text-blue-500">beta</sup></span></h1>
        <div className="flex items-center space-x-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveModule('log-analyzer')}
              className={`px-4 py-2 rounded ${
                activeModule === 'log-analyzer' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              Log Analyzer
            </button>
            <button
              onClick={() => setActiveModule('doc-analyzer')}
              className={`px-4 py-2 rounded ${
                activeModule === 'doc-analyzer' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              Documentation Analyzer
            </button>
          </div>
          <div className="border-l pl-4 flex space-x-2">
            <button
              onClick={() => setGlobalTab('settings')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
            >
              Settings
            </button>
            <button
              onClick={() => setGlobalTab('about')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
            >
              About
            </button>
          </div>
        </div>
      </div>

      {activeModule === 'log-analyzer' && (
        <>
          {/* Tabs */}
          <div className="flex border-b mb-6 flex-wrap">
            <button
              className={`py-2 px-4 mr-2 ${activeTab === 'upload' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
              onClick={() => setActiveTab('upload')}
            >
              Upload
            </button>
            <button
              className={`py-2 px-4 mr-2 ${activeTab === 'results' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
              onClick={() => setActiveTab('results')}
              disabled={!analysisResults}
            >
              Results
            </button>
            <button
              className={`py-2 px-4 mr-2 ${activeTab === 'history' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
            <button
              className={`py-2 px-4 mr-2 ${activeTab === 'statistics' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
              onClick={() => setActiveTab('statistics')}
            >
              Statistics
            </button>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'upload' && renderUploadTab()}
            {activeTab === 'results' && renderResultsTab()}
            {activeTab === 'history' && renderHistoryTab()}
            {activeTab === 'statistics' && renderStatisticsTab()}
          </div>

          {/* Detail Modal */}
          {showDetails && selectedItem && (
            <DetailModal 
              item={selectedItem} 
              onClose={closeDetails} 
            />
          )}
        </>
      )}

      {activeModule === 'doc-analyzer' && (
        <IsilonDocAnalyzer
          docData={docData}
          setDocData={setDocData}
          savedDocs={savedDocs}
          setSavedDocs={setSavedDocs}
          docName={docName}
          setDocName={setDocName}
          activeTab={docActiveTab}
          setActiveTab={setDocActiveTab}
          error={docError}
          setError={setDocError}
          showRawData={showRawData}
          setShowRawData={setShowRawData}
        />
      )}
      
      {renderGlobalTabs()}
    </div>
  );
};

export default IsilonLogAnalyzer;